<template>
  <v-container class="ma-0 pa-0" fluid v-resize="onResize">
    <v-navigation-drawer
      v-model="filters"
      class="toolbar"
      absolute
      temporary
      width="325"
      overlay-opacity="0">
      <v-list-item :class="label">
        Filters
        <v-spacer />
        <v-btn icon small @click="filters = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-list-item>
      <v-list class="pt-6">
        <v-list-item class="listItem">
          <v-text-field
            :class="body"
            outlined
            dense
            background-color="input"
            v-model="descriptionFilter"
            label="Description"
            prepend-inner-icon="mdi-filter-outline"
            @click:clear="descriptionFilter = ''"
            clearable />
        </v-list-item>
        <v-list-item class="listItem">
          <v-autocomplete
            :class="body"
            :menu-props="{ rounded: '' }"
            dense
            outlined
            item-text="storeTypeDescription"
            item-value="storeType"
            background-color="input"
            prepend-inner-icon="mdi-filter-outline"
            label="Store Type"
            clearable
            v-model="storeTypeFilter">
          </v-autocomplete>
        </v-list-item>
        <v-list-item class="listItem">
          <v-autocomplete
            :class="body"
            :menu-props="{ rounded: '' }"
            dense
            outlined
            background-color="input"
            prepend-inner-icon="mdi-filter-outline"
            label="Contract Type"
            clearable
            v-model="contractTypeFilter">
          </v-autocomplete>
        </v-list-item>
        <v-list-item class="listItem">
          <v-menu
            ref="menu"
            v-model="menu"
            return-value="dates"
            :close-on-content-click="false"
            nudge-top="25"
            transition="scale-y-transition"
            offset-y
            min-width="auto">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :class="body"
                outlined
                dense
                background-color="input"
                label="Date Range"
                prepend-inner-icon="mdi-filter-outline"
                v-bind="attrs"
                v-on="on"
                clearable
                @click:clear="dates = []" />
            </template>
            <v-date-picker
              :min="dateFilter[0]"
              range
              :show-current="false"
              v-model="dateFilter"
              no-title
              @click.stop="menu = false">
              <v-btn
                text
                class="text-caption text-capitalize"
                @click="dateFilter = []">
                Clear
              </v-btn>
            </v-date-picker>
          </v-menu>
        </v-list-item>
        <v-list-item class="listItem">
          <v-autocomplete
            :class="body"
            :menu-props="{ rounded: '' }"
            item-text="text"
            item-value="value"
            dense
            outlined
            background-color="input"
            prepend-inner-icon="mdi-filter-outline"
            label="Status"
            clearable
            v-model="statusFilter">
          </v-autocomplete>
        </v-list-item>
        <v-list-item>
          <v-spacer />
          <v-btn :class="body" text> Clear Filters </v-btn>
        </v-list-item>
      </v-list>
    </v-navigation-drawer>

    <v-data-table
      :items-per-page="pageSize"
      :headers="headers"
      :height="tableHeight"
      :items="reports"
      :class="table"
      style="cursor: pointer"
      class="rounded-0"
      dense
      :no-data-text="
        $auth.isBuyer && !vendor
          ? 'Select Vendor to Display Contracts'
          : !$auth.isBuyer && vendor
          ? 'Create Contract for Selected Vendor'
          : 'No Contracts to Display'
      "
      fixed-header
      hide-default-footer>
      <template #top>
        <v-toolbar flat color="toolbar" style="cursor: default">
          <v-col md="4" lg="3" xl="3">
            <v-autocomplete
              auto-select-first
              :class="body"
              class="mt-7"
              :menu-props="{ rounded: '', maxHeight: 515 }"
              dense
              outlined
              :items="vendors"
              item-text="vendor_name"
              item-value="vendor_number"
              background-color="input"
              :prepend-inner-icon="
                $auth.isBuyer ? 'mdi-filter-outline' : 'mdi-account-circle'
              "
              label="Vendor"
              v-model="vendor"
              return-object
              :filter="vendorFilter"
              @change="getReports">
              <template #item="data">
                <v-list-item-content>
                  <v-list-item-title>{{
                    data.item ? data.item.vendor_name : ''
                  }}</v-list-item-title>
                  <v-list-item-subtitle class="text-capitalize">{{
                    data.item ? data.item.vendor_number : ''
                  }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </v-col>
        </v-toolbar>
      </template>

      <template #[`item.vendor_number`]="{ item }">
        <span class="noWrap">{{ item.vendor_number }}</span>
      </template>
      <template #[`item.item_count`]="{ item }">
        <span class="noWrap">{{ item.item_count }}</span>
      </template>
      <template #[`item.description`]="{ item }">
        <span class="noWrap">{{ item.description }}</span>
      </template>
      <template #[`item.casepack`]="{ item }">
        <span class="noWrap">{{ item.casepack }}</span>
      </template>
      <template #[`item.casecost`]="{ item }">
        <span class="noWrap">{{ item.casecost }}</span>
      </template>
      <template #[`item.unitcost`]="{ item }">
        <span class="noWrap">{{ item.unitcost }}</span>
      </template>
      <template #[`item.retail`]="{ item }">
        <span class="noWrap">{{ item.retail }}</span>
      </template>
      <template #[`item.retailpercentage`]="{ item }">
        <span class="noWrap">{{ item.retailpercentage }}</span>
      </template>

      <template #footer>
        <v-divider />
        <v-toolbar height="59" flat style="cursor: default">
          <v-row dense align="center" justify="end" style="height: 59px">
            <v-spacer />
            <v-col xs="2" sm="2" md="2" lg="2" xl="2" align="end">
              <span class="text-caption">Rows per page:</span>
            </v-col>
            <v-col xs="2" sm="2" md="2" lg="1" xl="1">
              <v-select
                class="text-caption mx-6 mt-2"
                :items="pageSizes"
                v-model="pageSize">
              </v-select>
            </v-col>
            <v-col
              xs="2"
              sm="2"
              md="2"
              lg="1"
              xl="1"
              align="end"
              style="max-width: 100px">
              <v-btn
                :disabled="prev"
                height="36"
                width="36"
                icon
                class="mb-2 ml-1 mr-1"
                @click="page -= 1">
                <v-icon>mdi-chevron-left</v-icon>
              </v-btn>
              <v-btn
                :disabled="next"
                height="36"
                width="36"
                icon
                class="mb-2 ml-1"
                @click="page += 1">
                <v-icon>mdi-chevron-right</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-toolbar>
      </template>
    </v-data-table>

    <!-- <ContractCopy :value.sync="modal" :contract="contract"/> -->
    <Overlay />
    <!-- <POSExportsAlert> -->
  </v-container>
</template>

<script>
import { table } from '@/mixins/table-config.js'
import { format } from '@/mixins/format'
// import { polling } from '@/mixins/polling.js'
import { displayHelpers } from '@/mixins/display-helpers'
import { mapState } from 'vuex'
// import { debounce } from 'lodash'

export default {
  name: 'ContractList',

  components: {
    //ContractCopy: () => import('@/components/contract/ContractCopy'),
    Overlay: () => import('@/components/shared/Overlay')
    //POSExports: () => import('@/components/contract/alerts/POSExports')
  },

  mixins: [table, format, displayHelpers],

  data: () => ({
    component: '',
    componentKey: 0,
    contract: {},
    reports: [],
    contractTypeFilter: '',
    contractView: false,
    dateFilter: [],
    descriptionFilter: '',
    disableNext: false,
    disablePrevious: true,
    filters: false,
    hasActiveClusters: true,
    limit: '',
    menu: false,
    modal: false,
    offset: '',
    page: 1,
    pageSize: 50,
    pageSizes: [50, 100, 250],
    statusFilter: '',
    storeTypeFilter: '',
    tableHeight: '',
    vendor: {},
    vendors: []
  }),

  computed: {
    user() {
      return this.$auth.user
    },

    ...mapState(['storeTypes', '$auth.isBuyer']),

    allVendorsSelected() {
      return !this._.isEmpty(this.vendor)
        ? this.vendor.vendor_number === 'ALL_VENDORS'
        : false
    },

    headers() {
      const headers = [
        { sortable: false, class: 'accent white--text', width: '2%' },
        {
          text: 'Vendor Number',
          align: 'start',
          class: 'label accent font-weight-medium white--text',
          filterable: true,
          sortable: true,
          value: 'vendor_number',
          width: 'auto'
        },
        {
          text: 'Item Count',
          align: 'start',
          class: 'accent font-weight-medium white--text',
          filterable: true,
          sortable: true,
          value: 'item_count',
          width: 'auto'
        },
        {
          text: 'Description',
          align: 'start',
          class: 'accent font-weight-medium white--text',
          filterable: true,
          sortable: true,
          value: 'description',
          width: 'auto'
        },
        {
          text: 'Case Pack',
          align: 'center',
          class: 'accent font-weight-medium white--text',
          filterable: true,
          sortable: true,
          value: 'casepack',
          width: '100'
        },
        {
          text: 'Case Cost',
          align: 'center',
          class: 'accent font-weight-medium white--text',
          filterable: true,
          sortable: true,
          value: 'casecost',
          width: 'auto'
        },
        {
          text: 'Unit Cost',
          align: 'center',
          class: 'accent font-weight-medium white--text',
          filterable: true,
          sortable: true,
          value: 'unitcost',
          width: 'auto'
        },
        {
          text: 'Retail',
          align: 'center',
          class: 'accent font-weight-medium white--text',
          filterable: true,
          sortable: true,
          value: 'retail',
          width: '160'
        },
        {
          text: 'Retail Percentage',
          align: 'center',
          class: 'accent font-weight-medium white--text',
          filterable: true,
          sortable: true,
          value: 'retailpercentage',
          width: '150'
        }
      ]

      const vendorNameHeader = {
        text: 'Vendor Name',
        align: 'start',
        class: 'label accent font-weight-medium white--text',
        filterable: true,
        sortable: true,
        value: 'vendor_name',
        width: 'auto'
      }

      if (!this._.isEmpty(this.vendor)) {
        if (this.vendor.vendor_number == 'ALL_VENDORS') {
          headers.splice(1, 0, vendorNameHeader)
        }
      }

      return headers
    },

    prev() {
      return this.page === 1
    },

    next() {
      return this.reports.length < this.pageSize
    }
  },

  watch: {
    user: {
      handler(newValue) {
        if (newValue) {
          this.getVendors()
        }
      },
      immediate: true
    },

    vendor: {
      handler(newValue) {
        if (newValue) {
          localStorage.setItem('filter_vendor', JSON.stringify(newValue))
          this.$store.commit('SET_CURRENT_VENDOR', newValue)
        } else {
          this.reports = []
        }
      }
    },

    page: {
      handler() {
        this.loading = true
        this.getReports()
      }
    },

    pageSize: {
      handler() {
        this.page = 1
        this.loading = true
        this.getReports()
      }
    },

    filters: {
      immediate: true,
      handler() {
        if (this.filters) {
          this.$emit('close')
        }
      }
    },

    modal: {
      immediate: true,
      handler() {
        this.componentKey += 1
      }
    }
  },

  methods: {
    onResize() {
      this.tableHeight = window.innerHeight - 225
    },

    vendorFilter(item, queryText) {
      const name = item.vendor_name.toLowerCase()
      const number = item.vendor_number.toLowerCase()
      const searchText = queryText.toLowerCase()

      return name.indexOf(searchText) > -1 || number.indexOf(searchText) > -1
    },

    getIcon(status) {
      switch (status) {
        case 'draft':
          return 'mdi-progress-pencil'
        case 'submitted':
          return 'mdi-progress-check'
        case 'approved':
          return 'mdi-checkbox-marked-circle-outline'
        case 'declined':
          return 'mdi-progress-alert'
        case 'pos_pending':
          return 'mdi-progress-clock'
        case 'pos_generated':
          return 'mdi-cash-register'
      }
    },

    getColor(status) {
      switch (status) {
        case 'draft':
          return 'blue-grey lighten-1'
        case 'submitted':
          return 'amber darken-1'
        case 'approved':
          return 'green'
        case 'declined':
          return 'red'
        case 'pos_pending':
          return 'deep-orange darken-1'
        case 'pos_generated':
          return 'black'
      }
    },

    async getVendors() {
      this.$store.dispatch('setOverlay', { status: 'show', text: 'Loading...' })
      const request = {
        vendor_numbers: !this.$auth.isBuyer ? this.$auth.vendorNumbers : []
      }
      try {
        const { data } = await this.$vendors.getVendorInfo(request)
        this.vendors = data
        this.loading = false
        this.$store.dispatch('setOverlay', { status: 'hide' })
        //if (this.$auth.isBuyer) {
        //this.vendors.unshift({ vendor_name: 'ALL VENDORS', vendor_number: 'ALL_VENDORS'})
        //}
        //if (!this.vendor || !localStorage.filter_vendor) this.vendor = this.vendors[0]
        //this.getReports()
      } catch {
        this.$store.dispatch('setSnackbar', {
          status: 'error',
          text: 'Error Retrieving Vendors'
        })
      }
    },

    async getReports() {
      this.$store.dispatch('setOverlay', { status: 'show', text: 'Loading...' })
      this.limit = this.pageSize
      this.offset = (this.page - 1) * this.pageSize
      try {
        const response = await this.$contracts.getReports(
          this.vendor.vendor_number,
          this.offset,
          this.limit
        )
        this.reports = response.data
      } catch {
        this.$store.dispatch('setSnackbar', {
          status: 'error',
          text: 'Error Retrieving Reports'
        })
      } finally {
        this.loading = false
        this.$store.dispatch('setOverlay', { status: 'hide' })
      }
    }
  }
}
</script>
<style scoped>
.noWrap {
  white-space: nowrap;
}

.listItem {
  height: 60px;
}
</style>
